import React, { useState, useEffect, useRef } from 'react';
import { GEMINI_API_KEY_LINK } from '../utils/providers/gemini/client';
import { translations } from '../utils/translations';
import ModelCard from './ModelCard';
import { useStore } from '../store/useStore';
import Auth from './Auth';
import useCookieConsent from '../hooks/useCookieConsent';
import LegalTermsPopup from './LegalTermsPopup'; // Import LegalTermsPopup

// Neural Network Animation Components
class Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;

  constructor(width: number, height: number) {
    this.x = Math.random() * width;
    this.y = Math.random() * height;
    this.vx = (Math.random() - 0.5) * 0.3;
    this.vy = (Math.random() - 0.5) * 0.3;
    this.radius = Math.random() * 2 + 3;
    this.opacity = Math.random() * 0.5 + 0.2;
  }

  update(width: number, height: number) {
    this.x += this.vx;
    this.y += this.vy;

    if (this.x < 0 || this.x > width) this.vx *= -1;
    if (this.y < 0 || this.y > height) this.vy *= -1;
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(30, 30, 30, ${this.opacity})`;
    ctx.fill();
  }
}

const DotBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dots = useRef<Dot[]>([]);
  const animationFrame = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      dots.current = Array.from({ length: 80 }, () => new Dot(canvas.width, canvas.height));
    };

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(document.documentElement);
    resizeCanvas();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      dots.current.forEach((dot1, i) => {
        dots.current.slice(i + 1).forEach(dot2 => {
          const dx = dot1.x - dot2.x;
          const dy = dot1.y - dot2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(dot1.x, dot1.y);
            ctx.lineTo(dot2.x, dot2.y);
            ctx.strokeStyle = `rgba(100, 200, 255, ${1 - dist / 100})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        });
      });

      dots.current.forEach(dot => {
        dot.update(canvas.width, canvas.height);
        dot.draw(ctx);
      });

      animationFrame.current = requestAnimationFrame(animate);
    };

    animationFrame.current = requestAnimationFrame(animate);

    return () => {
      observer.disconnect();
      animationFrame.current && cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
    />
  );
};

const LandingPage: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const cookieConsent = useCookieConsent();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user } = useStore();
  const [showLegalTerms, setShowLegalTerms] = useState(false); // State for legal terms popup
  const legalTermsContent = `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Términos y Condiciones Legales</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
        }
        h1, h2, h3 {
            color: #333;
        }
        h1 {
            border-bottom: 2px solid #ccc;
            padding-bottom: 10px;
        }
        h2 {
            margin-top: 25px;
        }
        h3 {
            margin-top: 20px;
        }
        p {
            margin-bottom: 15px;
        }
        ul {
            margin-bottom: 15px;
            padding-left: 20px;
        }
        li {
            margin-bottom: 5px;
        }
    </style>
</head>
<body>
    <h1>POLÍTICA DE PROTECCIÓN DE DATOS</h1>
    <p>A continuación se adapta la presente política para reflejar que se trata de una aplicación web con múltiples chats, conectados a modelos externos a través de APIs, sobre los cuales 1Consulting BeintheG, S.L. (en adelante, “Multiusos Chat” o “el Titular”) no asume responsabilidad en cuanto al uso de la información enviada a dichas empresas: Anthropic, Deepseek, etc. en dichos chats. Al mismo tiempo, se detalla el tratamiento de los datos personales (nombres e e-mails) que sí son recabados directamente por la empresa para fines de comunicación y marketing, en cumplimiento de la normativa vigente, especialmente del Reglamento General de Protección de Datos (RGPD) y la legislación española aplicable.</p>

    <h2>1. RESPONSABLE DEL TRATAMIENTO</h2>
    <p>En cumplimiento de lo previsto en el artículo 5 de la LOPD y el artículo 13 del RGPD, se informa que los datos personales que se recaban directamente del interesado/a a través de los recursos disponibles en esta página web serán tratados de forma confidencial e incorporados a los ficheros de los que es responsable:</p>
    <p>1 Consulting BeintheG, S.L.<br>
    CIF B88171806<br>
    Domicilio social: C/ Andrés Mellado 79, 1ºA, 28003 Madrid (España)<br>
    Teléfono: +34 666 55 77 22<br>
    Correo electrónico: hola@memba.es</p>
    <p>Para cualquier comunicación con el Titular, podrá dirigirse al domicilio social, al teléfono de Atención al Cliente o a la dirección de correo electrónico indicada.</p>

    <h2>2. FINALIDAD DEL TRATAMIENTO</h2>
    <p>Los datos personales facilitados se tratarán con las siguientes finalidades:</p>
    <ul>
        <li><strong>Registro como usuario/a:</strong> Gestionar el alta de usuarios en la aplicación web.</li>
        <li><strong>Desarrollo y ejecución de servicios contratados:</strong> Incluye la compraventa de productos y/o servicios a través del sitio web, de acuerdo con los términos y condiciones aplicables.</li>
        <li><strong>Comunicaciones informativas y de marketing:</strong> Enviar, por medios electrónicos (email, SMS u otros), información relativa a los productos o servicios adquiridos, así como comunicaciones de marketing de la misma empresa Multiusos Chat.</li>
        <li><strong>Atención al cliente:</strong> Responder y dar soporte a las solicitudes enviadas a través de los formularios o métodos de contacto del sitio web.</li>
        </ul>
    <p><strong>Plazos de conservación:</strong> Se conservarán los datos mientras se mantenga la relación jurídica establecida y, en todo caso, hasta que el interesado/a revoque su consentimiento.</p>

    <h2>3. LEGITIMACIÓN DEL TRATAMIENTO</h2>
    <p><strong>Consentimiento y relación contractual:</strong> El tratamiento de datos se basa en el consentimiento otorgado por el interesado al rellenar los formularios de registro o compra y/o en la relación contractual resultante de la compraventa de productos y/o servicios.</p>
    <p><strong>Campos obligatorios:</strong> Algunos campos, como el nombre o el e-mail, son de cumplimentación obligatoria. Si dichos campos permanecen en blanco, no se podrá procesar el pedido o la solicitud.</p>

    <h2>4. DATOS Y CONTENIDOS COMPARTIDOS EN CHATS CON APIS EXTERNAS</h2>
    <p><strong>Integración de servicios de terceros:</strong> La aplicación web de Multiusos chat puede ofrecer funcionalidades de chat que utilizan modelos de inteligencia artificial provistos por servicios externos, a través de APIs.</p>
    <p><strong>Exclusión de responsabilidad:</strong> El Titular no se hace responsable del tratamiento, uso o conservación de los datos que el usuario/a introduzca en dichos chats externos, ni de las posibles consecuencias derivadas. Cada proveedor externo actúa, en su caso, como responsable o corresponsable de su propio tratamiento, de acuerdo con sus políticas de privacidad.</p>
    <p><strong>Recomendación al usuario/a:</strong> Se aconseja no facilitar información confidencial o sensible a través de estos chats externos. En caso de hacerlo, el usuario/a asume la responsabilidad de revisar las políticas de privacidad de dichos proveedores externos y actuar conforme a las mismas.</p>

    <h2>5. DESTINATARIOS DE CESIONES O TRANSFERENCIAS</h2>
    <p><strong>Datos gestionados por Multiusos Chat:</strong> Los datos personales recabados directamente (principalmente nombre y correo electrónico) no se comunicarán a terceros salvo obligación legal.</p>
    <p><strong>Procesadores de datos:</strong> En caso de utilizar servicios de terceros (p.ej., proveedores de hosting o plataformas de email marketing) para finalidades específicas, se formalizarán los correspondientes contratos de encargo de tratamiento conforme al RGPD.</p>

    <h2>6. DERECHOS DE LOS INTERESADOS</h2>
    <p><strong>Derechos ARCO-POL:</strong> Los interesados podrán ejercer sus derechos de acceso, rectificación, supresión, portabilidad, limitación y oposición al tratamiento, así como retirar el consentimiento en cualquier momento.</p>
    <p><strong>Procedimiento:</strong> Para ejercer dichos derechos o solicitar la baja de nuestras comunicaciones comerciales, podrá dirigir un correo electrónico a hola@memba.es indicando su voluntad. El Titular podrá requerirle la acreditación de su identidad.</p>
    <p><strong>Tutela ante la AEPD:</strong> Asimismo, el interesado tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos si considera que no se han atendido adecuadamente sus derechos.</p>

    <h2>7. AVISO LEGAL</h2>
    <p>A los efectos de la Ley 34/2002 (LSSI) y resto de normativa aplicable, se informa a los usuarios de lo siguiente:</p>
    <h3>7.1. Identificación del titular de la web</h3>
    <p>1 Consulting BeintheG, S.L.<br>
    CIF B88171806<br>
    Domicilio social: C/ Andrés Mellado 79, 1ºA, 28003 Madrid (España)<br>
    Teléfono: +34 666 55 77 22<br>
    Correo electrónico: soporte@memba.es</p>
    <h3>7.2. Uso de la página web</h3>
    <p>El usuario/a se compromete a utilizar el sitio web, sus servicios y contenidos de manera adecuada, lícita y conforme a la buena fe. Queda prohibido realizar acciones que causen daños o que impidan el normal disfrute de la web por parte de otros usuarios.</p>
    <h3>7.3. Protección de datos personales</h3>
    <p>Se aplican las disposiciones descritas en la Política de Protección de Datos.</p>
    <h3>7.4. Empleo de cookies</h3>
    <p>Se informa al usuario/a sobre la Política de Cookies, disponible en esta misma página, donde se detalla el uso, finalidad y procedimiento de gestión de las cookies.</p>
    <h3>7.5. Propiedad intelectual e industrial</h3>
    <p>Todo el contenido de la web (imágenes, software, textos, marcas, etc.) es titularidad exclusiva de Multiusos Chat o de sus licenciantes. Queda prohibida su reproducción o uso con fines comerciales, salvo autorización expresa.</p>
    <h3>7.6. Exclusión de garantías y responsabilidad</h3>
    <p>Multi Chat no se hace responsable de posibles daños o perjuicios derivados de errores en los contenidos, falta de disponibilidad o transmisión de virus, habiendo adoptado las medidas tecnológicas necesarias para evitarlos. Se reserva el derecho a realizar cambios en la web sin previo aviso.</p>
    <h3>7.7. Enlaces externos</h3>
    <p>Multiusos Chat no ejerce control sobre los sitios enlazados desde este portal ni asume responsabilidad por sus contenidos o políticas. La inclusión de un enlace no implica asociación ni participación con los titulares de dichos sitios.</p>
    <h3>7.8. Legislación aplicable y jurisdicción</h3>
    <p>Las partes se someten a la legislación española y a los Juzgados y Tribunales de Madrid capital, salvo que el usuario/a ostente la condición de Consumidor o Usuario; en tal caso, la competencia corresponderá a los juzgados del domicilio del usuario/a.</p>

    <h2>8. POLÍTICA DE COOKIES</h2>
    <p>En cumplimiento del artículo 22 de la LSSI y en relación con el RGPD, Multiusos Chat pone a disposición de los usuarios su Política de Cookies, donde se detalla la definición de cookies, los tipos utilizados y la forma de aceptarlas, configurarlas o bloquearlas.</p>

    <h2>CONCLUSIÓN Y CONSEJO PERSONAL</h2>
    <p>En definitiva, esta Política y Aviso Legal buscan proteger la privacidad de los usuarios, manteniendo la transparencia en cuanto al uso de datos e informando sobre los servicios externos (APIs de inteligencia artificial) que se utilizan en la aplicación. Mi consejo es que, antes de introducir información sensible en los chats con modelos externos, revises las políticas de privacidad de los proveedores para garantizar un uso responsable y seguro de tus datos. Además, recuerda que puedes ejercer en cualquier momento tus derechos de protección de datos, incluidas la rectificación y la supresión, así como darte de baja de las comunicaciones comerciales enviando un correo a hola@memba.es</p>
</body>
</html>`;

  return (
    <div className="min-h-screen bg-white">
      <DotBackground />
      <main className="relative z-10">
        <div className="absolute top-4 left-4 z-50">
          <img
            src="/icons/camelon-negro-logo-transparente.png"
            alt="Camelon Logo"
            className="h-12 md:h-16 w-auto"
          />
        </div>
        <div className="container mx-auto px-4 py-16">


          <div className="text-center mb-8">
            <h1 className="main-heading mt-16 md:mt-0">
              {language === 'en' ? (
                <>Welcome to <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-transparent bg-clip-text">Camaleon</span></>
              ) : (
                <>Bienvenido a <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-transparent bg-clip-text">Camaleon</span></>
              )}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              {translations[language].getStartedWithPowerfulAiModels}
            </p>
            <div className="w-full max-w-[1120px] h-[56.25vw] max-h-[630px] mx-auto rounded-lg overflow-hidden shadow-lg bg-black">
              <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src="/videos/demo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <ModelCard
              name="Anthropic"
              description="Powerful AI models for natural language processing"
              logo="/provider-icons/anthropic-logo.png"
              tier="Paid"
              apiKeyLink="https://console.anthropic.com/dashboard"
              tutorialLink="https://www.youtube.com/@tecnopadre"
            />
            <ModelCard
              name="Groq"
              description="High-performance AI acceleration platform"
              logo="/provider-icons/groq-logo.jpg"
              tier="Free"
              apiKeyLink="https://console.groq.com/keys"
              tutorialLink="https://youtu.be/Yh9vxwjEDcw"
            />
            <ModelCard
              name="OpenAI"
              description="Leading AI research and deployment company"
              logo="/provider-icons/openai-logo.png"
              tier="Paid"
              apiKeyLink="https://platform.openai.com/api-keys"
              tutorialLink="https://youtu.be/F8gfFzfFLwk"
            />
            <ModelCard
              name="DeepSeek"
              description="Advanced AI models for deep learning applications"
              logo="/provider-icons/deepseek-logo.webp"
              tier="Pro"
              apiKeyLink="https://platform.deepseek.com/api_keys"
              tutorialLink="https://www.youtube.com/@tecnopadre"
            />
            <ModelCard
              name="Google Gemini"
              description="Multimodal AI models for text and image understanding"
              logo="/provider-icons/gemini-new-dark-mode.webp"
              tier="Free"
              apiKeyLink={GEMINI_API_KEY_LINK}
              tutorialLink="https://www.youtube.com/@tecnopadre"
            />
            <ModelCard
              name="Ollama"
              description="Open-source framework for running large language models locally"
              logo="/provider-icons/ollama-logo.png"
              tier="Self-Hosted"
            />
            <ModelCard
              name="xAI"
              description="Explainable AI models for transparent decision making"
              logo="/provider-icons/xai-logo.webp"
              tier="Early Access"
              apiKeyLink="https://console.x.ai/team/fa03f3ac-aa17-4d29-b8a2-a8498267e55c/api-keys"
              tutorialLink="https://youtu.be/iep0D3lnRPE"
            />
            <ModelCard
              name="Cerebras"
              description="AI hardware and software solutions for deep learning"
              logo="/provider-icons/cerebras-logo.png"
              tier="Enterprise"
              apiKeyLink="https://cloud.cerebras.ai/platform/org_ed2xyytct4k4y5d5wyetrfev/apikeys"
              tutorialLink="https://youtu.be/NXInihBbvno"
            />
            <ModelCard
              name="MiniMax"
              description="Chinese AI company specializing in natural language processing"
              logo="/provider-icons/minimax-logo.jpg"
              tier="Enterprise"
              apiKeyLink="https://intl.minimaxi.com/user-center/basic-information/interface-key"
              tutorialLink="https://www.youtube.com/@tecnopadre"
            />
          </div>

          {/* New section with text content */}
          <div className="mt-16">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {language === 'en' ? (
                  'Discover an Intelligent Chat for Your Business or Family, Without Needing to Know About AI'
                ) : (
                  'Descubre un Chat Inteligente para Tu Empresa o Familia, Sin Necesidad de Saber de IA'
                )}
              </h2>
              <div className="text-gray-600 max-w-3xl mx-auto">
                {language === 'en' ? (
                  <>
                    <p>
                      Imagine a personalized chat (like chat.yourcompany.com) where everyone on your team or family can easily collaborate, even without understanding technology? With our platform, it's possible.
                    </p>
                    <h3>Specifications:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Simple and Uncomplicated</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Fair payment: You only pay for what you use, no fixed monthly fees</li>
                          <li>Full control: Parents or system administrators manage access</li>
                          <li>Multi-device sync across mobile, tablet, and computer</li>
                        </ul>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Answers Tailored to You</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Instantly switch between AI models mid-conversation</li>
                          <li>Learns from previous interactions to maintain context</li>
                          <li>Multilingual support with dark/light mode options</li>
                        </ul>
                      </div>

                      <div className="md:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Secure and Customizable</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Custom branding with company colors/logo</li>
                          <li>Usage analytics and privacy controls</li>
                          <li>Free basic models + premium specialized AI options</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">What Makes It Unique?</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>1-click answer copying for quick saving</li>
                          <li>Smart question formulation suggestions</li>
                          <li>Continuous conversation history across models</li>
                        </ul>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Perfect For Teams Needing</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Flexibility without technical barriers</li>
                          <li>Centralized, measurable efficiency</li>
                          <li>Accessible AI-powered innovation</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-4">
                      Ready to try it? Create your personalized chat in minutes and discover how AI can simplify your daily life.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      ¿Imaginas un chat personalizado (como chat.tuempresa.com) donde todos en tu equipo o familia puedan colaborar fácilmente, incluso sin entender de tecnología? Con nuestra plataforma, es posible.
                    </p>
                    <br></br>
                    <h3 className="section-heading">Características:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Simple y Sin Complicaciones</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Pago justo: Sin mensualidades, solo por uso</li>
                          <li>Control de acceso por responsables</li>
                          <li>Sincronización multi-dispositivo</li>
                        </ul>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Respuestas Adaptadas a Ti</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Cambia entre modelos de IA instantáneamente</li>
                          <li>Mejora continua aprendiendo de conversaciones</li>
                          <li>Multilingüe con modo oscuro/claro</li>
                        </ul>
                      </div>

                      <div className="md:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Seguro y a Tu Medida</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Personaliza con colores y logo corporativo</li>
                          <li>Analíticas de uso y control de privacidad</li>
                          <li>Modelos básicos gratuitos + IA especializada</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">¿Qué lo hace único?</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Copia respuestas con 1 clic instantáneo</li>
                          <li>Sugerencias inteligentes para formular preguntas</li>
                          <li>Historial continuo entre modelos de IA</li>
                        </ul>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <h3 className="section-heading">Perfecto Para Equipos Que Buscan</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                          <li>Flexibilidad sin barreras técnicas</li>
                          <li>Eficiencia centralizada y medible</li>
                          <li>Innovación accesible con IA potente</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-4">
                      ¿Listo para probarlo? Crea tu chat personalizado en minutos y descubre cómo la IA puede simplificar tu día a día.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="fixed top-4 right-4 inline-flex gap-2 md:gap-4 z-50">
              <div className="flex gap-2 md:gap-4 mr-auto">

                <button
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md transition-all ${language === 'en'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50'
                    }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md transition-all ${language === 'es'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'text-gray-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50'
                    }`}
                >
                  ES
                </button>
              </div>
              <button
                onClick={() => {
                  setShowAuth(true);
                  setAuthMode('login');
                }}
                className="bg-gradient-to-r from-blue-50 to-purple-50 text-gray-800 px-3 py-1.5 md:px-6 md:py-2.5 rounded-md hover:from-blue-100 hover:to-purple-100 transition-all text-xs md:text-base border border-blue-200"
              >
                {translations[language].login as string}
              </button>
              <button
                onClick={() => {
                  setShowAuth(true);
                  setAuthMode('signup');
                }}
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-3 py-1.5 md:px-6 md:py-2.5 rounded-md hover:opacity-90 transition-all text-xs md:text-base shadow-md"
              >
                {translations[language].signUp as string}
              </button>
            </div>
          </div>

          {showAuth && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white rounded-lg p-8 max-w-md w-full">
                <Auth
                  onBack={() => setShowAuth(false)}
                  initialMode={authMode === 'signup' ? false : true}
                  language={language}
                />
              </div>
            </div>
          )}
        </div>
      </main>
      <footer className="relative z-10 text-center py-4 text-gray-600 dark:text-gray-300">
        <button onClick={() => setShowLegalTerms(true)} className="hover:underline">
          Términos y Condiciones
        </button>
      </footer>
      {showLegalTerms && ( // Conditionally render LegalTermsPopup
        <LegalTermsPopup
          content={legalTermsContent}
          onClose={() => setShowLegalTerms(false)}
        />
      )}
    </div>
  );
};

export default LandingPage;